import React, { useEffect, useRef, useState } from 'react';
import { 
	AccountCircle, 
	ErrorOutline, 
	Send, 
	Add, 
	AttachMoney, 
	Description as DescriptionIcon, 
	Assignment, 
	TimerOff as TimerOffIcon
} from '@material-ui/icons';
import {
	ChatHeader,
	ChatHeaderInfo,
	ChatMessages,
	Message,
	MessageContent,
	MessageTimestamp,
	ChatInputForm,
	ChatInput,
	SendButton,
	DateSeparator,
	CloseButton,
	ButtonSelectOperator,
	LoadMoreButton,
	LoadingMessageContainer,
	ChatEndedMessage,
	ActionButton,
	ActionDialog,
	ActionList,
	ActionItem,
} from './styles';
import { formatDate } from '../../utils/dateFormatters';
import { ArrowDropDown } from '@mui/icons-material';
import Modal from 'newComponents/Modal';
import { useChatArea } from './hooks/useChatArea.hooks';
import SelectSearch from 'newComponents/SelectSearch';
import { BootstrapInput } from 'app/routes/CaseDetails/CaseCard/NegotiationTab/styles';
import { useQuery } from 'react-query';
import { useMessage } from '../../context/MessageContext';
import { useWhatsappChatStore } from 'store/whatsappChat/useWhatsappChatStore';
import { ChatActions } from '../ChatActions';
import DialogComponent from 'newComponents/DialogComponent';
import { useSelector } from 'react-redux';
import { useNotifyAlert } from 'context/NotifyAlert';

const ChatArea = ( {
	useForm,
} ) => {
	const { fetchMessages, messagesCursor, messages, handleSendMessage, closeChat, assignChat, getTemplates, sendTemplateMessage } = useMessage();
	const userEmail = useSelector( state => state.auth.userLogin );
	const { closeWhatsappChat } = useWhatsappChatStore( ( state ) => state.actions );
	const { setShowNotify } = useNotifyAlert();
	const [ isLoading, setIsLoading ] = useState( false );
	const [ shouldScrollToBottom, setShouldScrollToBottom ] = useState( false );

	const [ loadingCloseService, setLoadingCloseService ] = useState( false );
	const [ loadingAssignChat, setLoadingAssignChat ] = useState( false );
	const [ openDialog, setOpenDialog ] = useState( false );
	const [ chatIdInput, setChatIdInput ] = useState( '' );
	const { isAdmin, userId, handleOpenModal, handleCloseModal, visible, handleFetchOperators, handleUpdateOperator, selectedChat } = useChatArea();
	const [ openActionDialog, setOpenActionDialog ] = useState( false );
	const actionButtonRef = useRef( null );
	const [ actionDialogPosition, setActionDialogPosition ] = useState( { top: 0, left: 0 } );
	const textareaRef = useRef( null );

	const messageValue = useForm.watch( 'message' );

	const resizeTextarea = () => {
		const textarea = textareaRef.current;
		if ( textarea ) {
			textarea.style.height = 'auto'; // Reset height
			textarea.style.height = `${Math.min( textarea.scrollHeight, 300 )}px`;
		}
	};

	useEffect( () => {
		resizeTextarea();
	}, [ messageValue ] );

	const handleChange = ( e ) => {
		useForm.setValue( 'message', e.target.value );
	};

	const { data: operators } = useQuery( {
		queryKey : [ 'operators' ],
		queryFn  : () => handleFetchOperators()
	} );

	const chatRef = useRef( null );
	const isInitialLoad = useRef( true );
	const isCaseAssigned = !!selectedChat?.caseId;
	const isConversationEnded = !!selectedChat?.closedAt;

	const userPermittedToAssignChat = !selectedChat?.userId ? false : selectedChat?.userEmail === userEmail ? false : !isAdmin;

	const isUserResponsible = selectedChat?.userEmail === userEmail;
	const hasCaseId = !!selectedChat?.caseId;
	const isActionButtonEnabled = isUserResponsible && hasCaseId;

	useEffect( () => {
		if ( chatRef.current && selectedChat && messages.length > 0 ) {
			setTimeout( () => {
				chatRef.current.scrollTop = chatRef.current.scrollHeight;
			}, 100 );
		}
		if ( selectedChat && selectedChat?.caseId ) {
			getTemplates( {
				caseId      : parseInt( selectedChat.caseId?.replace( /\D/g, '' ) ),
				templateIds : [ 1, 2, 3, 4 ]
			} );
		}
	}, [ selectedChat ] );

	const formatMessageContent = ( text ) => {
		const parts = text.split( /(\*[^*]+\*)/ );
		return parts.map( ( part, index ) => {
			if ( part.startsWith( '*' ) && part.endsWith( '*' ) ) {
				return <strong key={index}>{part.slice( 1, -1 )}</strong>;
			}
			return part;
		} );
	};

	const groupMessagesByDate = ( messages ) => {
		const groups = {};
		messages.forEach( message => {
			const date = new Date( message.createdAt ).toDateString();
			if ( !groups[date] ) {
				groups[date] = [];
			}
			groups[date].push( message );
		} );
		return groups;
	};

	const handleOpenAssignDialog = () => {
		setOpenDialog( ( prev ) => !prev );
	};

	const handleOpenActionDialog = () => {
		if ( openActionDialog ) {
			handleCloseActionDialog();
			return;
		}
		
		if ( actionButtonRef.current ) {
			const buttonRect = actionButtonRef.current.getBoundingClientRect();
			setActionDialogPosition( {
				top  : buttonRect.top + window.scrollY - 200,
				left : buttonRect.left + window.scrollX
			} );
		}
		setOpenActionDialog( true );
	};

	const handleCloseActionDialog = () => {
		setOpenActionDialog( false );
	};

	const handleLoadMessages = async () => {
		if ( !chatRef.current ) return;
		setIsLoading( true );

		const chatDiv = chatRef.current;
		const previousScrollHeight = chatDiv.scrollHeight;
		const previousScrollTop = chatDiv.scrollTop;

		await fetchMessages();

		setIsLoading( false );

		chatDiv.scrollTop = chatDiv.scrollHeight - previousScrollHeight + previousScrollTop;
	};

	const handleSendMessageWithScroll = async ( e, useForm, userEmail ) => {
		await handleSendMessage( e, useForm, userEmail );
		setShouldScrollToBottom( true );
	};

	const handleAssignChat = async () => {
		try {
			setLoadingAssignChat( true );
			await assignChat( selectedChat?.id, userId, userEmail, chatIdInput );
			handleOpenAssignDialog();
			setChatIdInput( '' );
		} finally {
			setLoadingAssignChat( false );
		}
	};

	const handleCloseService = async () => {
		if ( selectedChat?.userEmail !== userEmail ) {
			setShowNotify( {
				status : 'error',
				text   : 'Apenas o responsável pelo chat pode finalizá-lo.'
			} );
			return;
		}

		setLoadingCloseService( true );
		await closeChat( isCaseAssigned, selectedChat?.id );
		setLoadingCloseService( false );
	};

	useEffect( () => {
		if ( chatRef.current && ( shouldScrollToBottom || isInitialLoad.current ) ) {
			chatRef.current.scrollTop = chatRef.current.scrollHeight;
			setShouldScrollToBottom( false );
			isInitialLoad.current = false;
		}
	}, [ messages, shouldScrollToBottom ] );

	const handleKeyDown = ( e ) => {
		if ( e.key === 'Enter' && !e.shiftKey ) {
			e.preventDefault();
			useForm.handleSubmit( event => handleSendMessageWithScroll( event, useForm, selectedChat.userEmail ) )( e );
		}
	};

	const handleSetMessage = ( message ) => {
		useForm.setValue( 'message', message );
	};

	// Agrupa as mensagens por data
	const groupedMessages = groupMessagesByDate( messages );
	const messageDates = Object.keys( groupedMessages );

	useEffect( () => {
		const handleClickOutside = ( event ) => {
			if ( openActionDialog ) {
				const dialogElement = document.querySelector( '.action-dialog' );
				if ( dialogElement && !dialogElement.contains( event.target ) && 
					actionButtonRef.current && !actionButtonRef.current.contains( event.target ) ) {
					handleCloseActionDialog();
				}
			}
		};

		document.addEventListener( 'mousedown', handleClickOutside );
		return () => {
			document.removeEventListener( 'mousedown', handleClickOutside );
		};
	}, [ openActionDialog ] );

	return (
		<div style={{
			width         : '100%',
			height        : '100%',
			display       : 'flex',
			flexDirection : 'column',
			boxSizing     : 'border-box'
		}}>
			<ChatHeader>
				<ChatHeaderInfo>
					{selectedChat ? (
						<>
							{selectedChat.profilePicUrl ? (
								<img
									src={selectedChat.profilePicUrl}
									alt="Contact"
									width={32}
									height={32}
									className="w-full h-full rounded-circle"
								/>
							) : (
								<AccountCircle className="w-16 h-16 text-zinc-400" />
							)}
							<div className="flex flex-col gap-2">
								<h3 className="text-white">{selectedChat.name || selectedChat?.id?.split( '@' )[0]}</h3>
								<p className="text-gray-300">{selectedChat.number}</p>

								<div className="flex flex-row gap-2">
									<p className="d-flex flex-row align-items-center text-gray-300 text-sm">Atendido por:
										<ButtonSelectOperator className='ml-2' onClick={handleOpenModal}>{selectedChat?.userEmail ?? 'Nenhum responsável'} <ArrowDropDown /></ButtonSelectOperator>
									</p>
								</div>
							</div>
						</>
					) : (
						<div className="flex flex-col gap-2">
							<h3 className="text-white">Não atribuído</h3>
						</div>
					)}
				</ChatHeaderInfo>
				<CloseButton onClick={closeWhatsappChat}>×</CloseButton>
			</ChatHeader>
			<ChatActions
				loadingCloseService={loadingCloseService}
				isCaseAssigned={isCaseAssigned}
				isConversationEnded={isConversationEnded}
				onEndConversation={handleCloseService}
				onAssignCase={handleOpenAssignDialog}
				caseId={selectedChat?.caseId}
			/>

			<DialogComponent
				confirmation={openDialog}
				title={'Insira o id do caso'}
				subText={'Digite um id de pelo menos 7 dígitos para vincular ao chat.'}
				closeFunction={handleOpenAssignDialog}
				callback={handleAssignChat}
				loading={loadingAssignChat}
				disabled={loadingAssignChat}
			>
				<input
					className="h-10 w-full rounded-md border border-gray-200 bg-white px-3 py-2 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-gray-400"
					placeholder={'Digite o id do caso aqui'}
					value={chatIdInput}
					onChange={( e ) => setChatIdInput( e.target.value )}
					disabled={loadingAssignChat}
				/>
			</DialogComponent>

			<ChatMessages ref={chatRef} isEnded={isConversationEnded}>
				{/* Botão "Carregar Mais" apenas uma vez no topo */}
				{!isLoading && messagesCursor && (
					<LoadMoreButton type='button' onClick={handleLoadMessages}>
						Carregar Mais
					</LoadMoreButton>
				)}
				{isLoading && (
					<LoadingMessageContainer>
						<MessageTimestamp>Carregando mensagens...</MessageTimestamp>
					</LoadingMessageContainer>
				)}
				
				{/* Renderiza as mensagens agrupadas por data */}
				{messageDates.map( ( date ) => (
					<React.Fragment key={date}>
						<DateSeparator>
							{formatDate( groupedMessages[date][0].createdAt )}
						</DateSeparator>
						{groupedMessages[date].map( ( message ) => (
							<Message key={message.id} sender={message.type}>
								{message?.status === 99 && <ErrorOutline title='Erro ao enviar mensagem' className='text-red' />}
								<MessageContent sender={message.type}>
									<div className='flex flex-row gap-2'>
										<p style={{ whiteSpace: 'pre-wrap' }}>{formatMessageContent( message.content )}</p>
									</div>
									<MessageTimestamp>
										{new Date( message.createdAt ).toLocaleTimeString( [], {
											hour     : '2-digit',
											minute   : '2-digit',
											hour12   : false,
											timeZone : 'UTC'
										} )}
									</MessageTimestamp>
								</MessageContent>
							</Message>
						) )}
					</React.Fragment>
				) )}
				
				{isConversationEnded && (
					<Message key='ended-message'>
						<ChatEndedMessage>
							<p>Esta conversa foi finalizada</p>
						</ChatEndedMessage>
					</Message>
				)}
			</ChatMessages>

			<ChatInputForm onSubmit={useForm.handleSubmit( e => handleSendMessageWithScroll( e, useForm, selectedChat.userEmail ) )}>
				<ActionButton 
					ref={actionButtonRef}
					onClick={handleOpenActionDialog}
					disabled={!isActionButtonEnabled}
					type='button'
					title={!isActionButtonEnabled ? 'Você precisa ser o responsável pelo chat e ter um caso vinculado para usar esta função' : ''}
				>
					<Add fontSize="small" />
				</ActionButton>
				<ChatInput
					ref={textareaRef}
					rows={1}
					disabled={selectedChat.userId !== userId || isConversationEnded}
					type="text"
					placeholder="Escreva sua mensagem..."
					value={messageValue}
					onChange={handleChange}
					onKeyDown={handleKeyDown}
					style={{ maxHeight: '150px', overflowY: 'auto' }}
				/>
				<SendButton
					type="submit"
					disabled={selectedChat.userId !== userId || isConversationEnded}
				>
					<Send fontSize='small' className="text-white" />
				</SendButton>
			</ChatInputForm>

			{/* Dialog de ações */}
			{openActionDialog && (
				<ActionDialog 
					className="action-dialog"
					style={{ 
						top       : `${actionDialogPosition.top}px`, 
						left      : `${actionDialogPosition.left}px`,
						transform : 'none'
					}}
				>
					<ActionList>
						<ActionItem onClick={() => sendTemplateMessage( 'interesse', handleSetMessage )}>
							<DescriptionIcon fontSize="small" style={{ marginRight: '12px' }} />
							Enviar mensagem de interesse
						</ActionItem>
						<ActionItem onClick={() => sendTemplateMessage( 'proposta', handleSetMessage )}>
							<Assignment fontSize="small" style={{ marginRight: '12px' }} />
							Enviar mensagem de proposta
						</ActionItem>
						<ActionItem onClick={() => sendTemplateMessage( 'cobrança de minuta', handleSetMessage )}>
							<AttachMoney fontSize="small" style={{ marginRight: '12px' }} />
							Enviar cobrança de minuta
						</ActionItem>
						<ActionItem onClick={() => sendTemplateMessage( 'ausência de resposta', handleSetMessage )}>
							<TimerOffIcon fontSize="small" style={{ marginRight: '12px' }} />
							Ausência de resposta
						</ActionItem>
					</ActionList>
				</ActionDialog>
			)}

			{/* Modal para selecionar o responsável */}
			<Modal visible={visible} onClose={handleCloseModal}>
				<div style={{ width: '500px', minWidth: '90%' }}>
					<h6>Selecione o responsável</h6>
					<SelectSearch
						variant={'outlined'}
						fullWidth
						disabled={ userPermittedToAssignChat || isConversationEnded }
						size='small'
						options={operators?.map( operator => ( { label: `${operator.name} - ${operator.email}`, value: operator.id } ) ) ?? []}
						selectedState={selectedChat.userId}
						setSelectedState={( id ) => {
							handleUpdateOperator( selectedChat.id, id, operators.find( operator => operator.id === id ).email );
						}}
						input={<BootstrapInput />}
					/>
				</div>
			</Modal>
		</div>
	);
};

export { ChatArea };
